import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import axios from 'axios';
import config from '../../../../config.json';
import { NotificationManager } from 'react-notifications';
import CustomDatePicker from '../../../SharedComponents/CustomDatePicker';

export default function AddTelecomExam({ show, handleClose, patientId, onExamAdded }) {
    const [loading, setLoading] = useState(false);
    const [patient, setPatient] = useState({});
    const [devices, setDevices] = useState([]);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        patient_id: '',
        birth_date: '',
        sex: '',
        device_uuid: '',
        device_label: '',
        scheduled_date: new Date(),
        end_scheduled_date: null,
        size: '',
        weight: ''
    });

    useEffect(() => {
        if (show && patientId) {
            getPatient();
            getDevices();
        }
    }, [show, patientId]);

    const getPatient = () => {
        setLoading(true);
        axios({
            method: 'get',
            url: `${config.SERVER_URL}/patients/${patientId}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': localStorage.getItem('token')
            }
        })
            .then(res => {
                setPatient(res.data);

                // Extract first_name and last_name from name_latin
                const nameParts = res.data.name_latin ? res.data.name_latin.split(' ') : ['', ''];
                const firstName = nameParts[0] || '';
                const lastName = nameParts.slice(1).join(' ') || '';

                // Convert gender to sex code
                let sex = '';
                if (res.data.gender === 'маж') {
                    sex = 'M';
                } else if (res.data.gender === 'жена') {
                    sex = 'F';
                }

                // Format birth_date as YYYY-MM-DD
                let birthDate = '';
                if (res.data.date_of_birth) {
                    const d = new Date(res.data.date_of_birth);
                    const year = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    birthDate = `${year}-${month}-${day}`;
                }

                setFormData(prev => ({
                    ...prev,
                    first_name: firstName || '',
                    last_name: lastName || '',
                    patient_id: res.data.id ? res.data.id.toString() : '',
                    birth_date: birthDate || '',
                    sex: sex || '',
                    size: res.data.height ? res.data.height.toString() : '',
                    weight: res.data.weight ? res.data.weight.toString() : ''
                }));
            })
            .catch(err => {
                NotificationManager.error('Грешка при вчитување на пациент');
            })
            .finally(() => setLoading(false));
    };

    const getDevices = () => {
        axios({
            method: 'get',
            url: `${config.SERVER_URL}/telekom/devices`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': localStorage.getItem('token')
            }
        })
            .then(res => {
                if (res.data && Array.isArray(res.data.Devices)) {
                    console.log("res.data", res.data);
                    setDevices(res.data.Devices);
                }
            })
            .catch(err => {
                console.error('Error fetching devices:', err);
                NotificationManager.error('Грешка при вчитување на уреди');
            });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // If device is selected, also capture the label
        if (name === 'device_uuid') {
            const selectedDevice = devices.find(d => d.Device_UUID === value);
            setFormData({
                ...formData,
                device_uuid: value,
                device_label: selectedDevice ? selectedDevice.label : ''
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const handleDateChange = (name, date) => {
        setFormData({
            ...formData,
            [name]: date
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.device_uuid || !formData.scheduled_date) {
            NotificationManager.error('Пополнете ги задолжителните полиња');
            return;
        }

        setLoading(true);

        // Format scheduled_date as YYYY-MM-DDTHH:mm:ss
        const formatDateTime = (date) => {
            if (!date) return null;
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        };

        const payload = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            patient_id: formData.patient_id.toString(),
            birth_date: formData.birth_date,
            sex: formData.sex,
            size: formData.size,
            weight: formData.weight,
            device_uuid: formData.device_uuid,
            device_label: formData.device_label,
            scheduled_date: formatDateTime(formData.scheduled_date),
            end_scheduled_date: formatDateTime(formData.end_scheduled_date)
        };

        console.log("Payload", payload);

        axios({
            method: 'post',
            url: `${config.SERVER_URL}/exams/telecom/add`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': localStorage.getItem('token')
            },
            data: payload
        })
            .then(res => {
                NotificationManager.success('Telecom прегледот е успешно креиран');
                onExamAdded();
                handleClose();
                resetForm();
            })
            .catch(err => {
                NotificationManager.error(err.response?.data || 'Грешка при креирање на преглед');
            })
            .finally(() => setLoading(false));
    };

    const resetForm = () => {
        setFormData({
            device_uuid: '',
            scheduled_date: new Date(),
            end_scheduled_date: null,
            size: '',
            weight: ''
        });
    };

    const handleModalClose = () => {
        resetForm();
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleModalClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Креирај Telecom Преглед</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label>Име</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.first_name}
                                    disabled
                                    readOnly
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label>Презиме</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.last_name}
                                    disabled
                                    readOnly
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label>Patient ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.patient_id}
                                    disabled
                                    readOnly
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group>
                                <Form.Label>Датум на раѓање</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.birth_date}
                                    disabled
                                    readOnly
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group>
                                <Form.Label>Пол</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.sex}
                                    disabled
                                    readOnly
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label>Висина (cm)</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="size"
                                    value={formData.size}
                                    onChange={handleInputChange}
                                    placeholder="175"
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label>Тежина (kg)</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="weight"
                                    value={formData.weight}
                                    onChange={handleInputChange}
                                    placeholder="70"
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-12">
                            <Form.Group>
                                <Form.Label>Device UUID <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    name="device_uuid"
                                    value={formData.device_uuid}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Избери уред...</option>
                                    {devices.map((device) => (
                                        <option key={device.Device_UUID} value={device.Device_UUID}>
                                            {device.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label>Закажан датум <span className="text-danger">*</span></Form.Label>
                                <CustomDatePicker
                                    startDate={formData.scheduled_date}
                                    setStartDate={(date) => handleDateChange('scheduled_date', date)}
                                    showTimeSelect={true}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label>Краен датум</Form.Label>
                                <CustomDatePicker
                                    startDate={formData.end_scheduled_date}
                                    setStartDate={(date) => handleDateChange('end_scheduled_date', date)}
                                    showTimeSelect={true}
                                />
                            </Form.Group>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleModalClose}>
                    Откажи
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                    style={{ backgroundColor: "#39828f", borderColor: "#39828f" }}
                >
                    {loading ? 'Се зачувува...' : 'Зачувај'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
